import React from "react";
import { CacheProvider } from "@emotion/react";
import createEmotionCache from "./src/utils/createEmotionCache";
import reporter from "./src/utils/reporter";
import {
  setReferrer,
  setReferrerId,
  trackPageView,
} from "./src/utils/analytics";
import { getQueryParam } from "./src/utils/uri";

const clientCache = createEmotionCache();

export const wrapRootElement = ({ element }) => {
  return <CacheProvider value={clientCache}>{element}</CacheProvider>;
};

export const onClientEntry = () => {
  // Seed the referrer before React mounts, so events fired during the first
  // render are attributed — form_view above all, since it fires from a mount
  // effect and useReferrer cannot have resolved the real referrer by then.
  //
  // Sources, richest first:
  //
  //   localStorage "referrer"  a record resolved on an earlier visit. The only
  //                            seed carrying sourceId and the partner's name,
  //                            and the common case for returning traffic.
  //   ?ref                     a fresh arrival, id only.
  //   sessionStorage "refId"   a later page in a ?ref session, id only — see
  //                            plugins/gatsby-plugin-referrer.
  //
  // `ttl` is an absolute expiry timestamp; useReferrer owns the record and
  // clears it once past. Checked here too, so a record useReferrer is about to
  // discard on this very load cannot attribute the first render to a partner
  // the visitor has aged out of. A record with no ttl counts as live, matching
  // how useReferrer treats one.
  try {
    const stored = JSON.parse(localStorage.getItem("referrer"));

    if (stored && stored.id && !(stored.ttl <= Date.now())) {
      setReferrer(stored);
      return;
    }
  } catch (_) {}

  try {
    setReferrerId(getQueryParam("ref") || sessionStorage.getItem("refId"));
  } catch (_) {}
};

// GA4 is configured with send_page_view: false, so every page view including
// the first one is sent from here.
export const onRouteUpdate = ({ location }) => {
  // Gatsby applies the new document title in an effect after the route renders,
  // so sending immediately would report the previous page's title. A macrotask
  // is enough to let that flush.
  setTimeout(() => {
    trackPageView({
      pathname: location.pathname,
      search: location.search,
      title: document.title,
    });
  }, 32);
};

export const onCaughtError = ({ error }) => {
  if (error) {
    reporter.report(error);
  }
};

export const onUncaughtError = ({ error }) => {
  if (error) {
    reporter.report(error);
  }
};
